<template>
    <v-container class="pa-5" fluid>
        <CreateOrderByCampaignProductDialog
            :is-open="showOrderByCampaignProductDialog"
            :products="preparedProducts"
            @on-close="closeCreateOrderByCampaignProductDialog"
            @create-order-by-product="createOrderByProductDialog"
        />
        <ConfirmDialog
            :is-open="showConfirmDialog"
            :data="confirmDialogData"
            @on-confirm="confirmDialogConfirm"
            @on-abort="confirmDialogClose"
        >
            <template #title>
                {{ confirmDialogData.title }}
            </template>
            <template #content>
                {{ confirmDialogData.content }}
            </template>
        </ConfirmDialog>
        <page-title>Edycja kampanii JMD</page-title>
        <v-row align="center"> 
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row tag="form">
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Parametry kampanii
                            </h2>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <table class="table table--simple vertical-align">
                                                    <tbody>
                                                        <tr>
                                                            <th>Data utworzenia:</th>
                                                            <td>{{ formatDate(campaign.createdAt) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Nazwa:</th>
                                                            <td>{{ campaign.name }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Numer:</th>
                                                            <td>{{ campaign.number }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Status:</th>
                                                            <td>{{ campaign.status.name }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Raport:</th>
                                                            <td>
                                                                <div class="d-inline-flex">
                                                                    <v-tooltip :open-delay="tooltipDelay" left>
                                                                        <template #activator="{on}">
                                                                            <v-btn
                                                                                icon
                                                                                text
                                                                                :disabled="loading"
                                                                                v-on="on"
                                                                                @click="generateReport()"
                                                                            >
                                                                                <v-icon right color="green">
                                                                                    mdi-file-excel
                                                                                </v-icon>
                                                                            </v-btn>
                                                                        </template>
                                                                        <span>Generuj raport</span>
                                                                    </v-tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Etykiety:</th>
                                                            <td>
                                                                <div class="d-inline-flex">
                                                                    <v-tooltip :open-delay="tooltipDelay" left>
                                                                        <template #activator="{on}">
                                                                            <v-btn
                                                                                icon
                                                                                text
                                                                                :disabled="loading"
                                                                                v-on="on"
                                                                                @click="generateLabelsReport()"
                                                                            >
                                                                                <v-icon right color="green">
                                                                                    mdi-file-excel
                                                                                </v-icon>
                                                                            </v-btn>
                                                                        </template>
                                                                        <span>Generuj etykiety</span>
                                                                    </v-tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-center">
                            <v-row class="d-flex align-center justify-center flex-wrap w-full">
                                <v-col>
                                    <v-btn
                                        color="info"
                                        block
                                        x-large
                                        class="mb-1"
                                        data-sentry-label="Nowe zamówienie"
                                        :disabled="!preparedProducts.length"
                                        @click="openCreateOrderByCampaignProductDialog"
                                    >
                                        Nowe zamówienie
                                    </v-btn>
                                </v-col>
                                <v-col>
                                    <v-btn
                                        color="error"
                                        block
                                        x-large
                                        class="mb-1"
                                        data-sentry-label="Zamknij kampanię"
                                        :loading="loadingCampaignClose"
                                        :disabled="isCampaignClosed || !campaign.mayBeClosed"
                                        @click="tryCloseCampaign(campaign)"
                                    >
                                        Zamknij kampanię
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-tabs
                                    v-model="tabs"
                                    slider-color="blue"
                                    grow
                                    background-color="grey lighten-4"
                                >
                                    <v-tab>
                                        Produkty
                                    </v-tab>
                                    <v-tab>
                                        Zestawy
                                    </v-tab>
                                    <v-tab>
                                        Centra dystrybucji
                                    </v-tab>
                                    <v-tabs-items v-model="tabs">
                                        <v-divider />
                                        <v-tab-item>
                                            <ProductsList
                                                :loading="loading"
                                                :products="campaign.products"
                                                :campaign-id="campaign.id"
                                                :is-edit-allowed="!isCampaignClosed"
                                                @product-changed="productChanged"
                                            />
                                        </v-tab-item>
                                        <v-tab-item>
                                            <SetsList
                                                :loading="loading"
                                                :product-sets="campaign.productSets"
                                                :product-list="campaign.products"
                                                :campaign-id="campaign.id"
                                                :is-edit-allowed="!isCampaignClosed"
                                                @set-changed="setChanged"
                                            />
                                        </v-tab-item>
                                        <v-tab-item>
                                            <DistributionCentersList
                                                :loading="loading"
                                                :distribution-centers="campaign.distributionCenters"
                                                :product-sets="campaign.productSets"
                                                :campaign-id="campaign.id"
                                                :is-edit-allowed="!isCampaignClosed"
                                                @distribution-center-changed="distributionCenterChanged"
                                            />
                                        </v-tab-item>
                                    </v-tabs-items>
                                </v-tabs>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>

import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import dateFormatter from '@/mixins/dateFormatter'
import { errorHandler } from '@/utils/errorHandler'
import ProductsList from '@/views/Campaigns/Campaigns/ProductsList'
import SetsList from '@/views/Campaigns/Campaigns/SetsList'
import DistributionCentersList from '@/views/Campaigns/Campaigns/DistributionCentersList'
import CreateOrderByCampaignProductDialog from '@/views/Campaigns/Dialogs/CreateOrderByCampaignProductDialog'
import tooltipDelay from '@/mixins/tooltip'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
    name: 'Campaign',
    components: {
        PageTitle,
        ProductsList,
        SetsList,
        DistributionCentersList,
        CreateOrderByCampaignProductDialog,
        ConfirmDialog,
    },
    mixins: [ dateFormatter, tooltipDelay ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('campaigns/getCampaign', to.params.id)
            .then(response => {
                next(vm => {
                    vm.campaign = response.data
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            campaign: {
                id: 0,
                name: null,
                number: null,
                status: {
                    name: null,
                },
                createdAt: null,
                products: [],
                productSets: [],
                distributionCenters: [],
            },
            tabs: 0,
            loading: false,
            showOrderByCampaignProductDialog: false,
            showConfirmDialog: false,
            loadingCampaignClose: false,
            confirmDialogData: {
                confirm: () => {},
            },
        }
    },
    computed: {
        preparedProducts() {
            return this.campaign.products.filter(product => product.canBeOrdered)
        },
        userEmail() {
            return this.$store.state.user.userEmail
        },
        isCampaignClosed() {
            return this.campaign.status.id === 30
        },
    },
    methods: {
        close() {
            this.$router.push({ name: 'campaignsCampaigns'})
                .catch(error => {
                    errorHandler(error)
                })
        },
        getCampaign(callback = null) {
            this.loading = true
            store.dispatch('campaigns/getCampaign', this.$route.params.id)
                .then(response => {
                    this.campaign = response.data
                    callback && callback()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        productChanged(callback = null) {
            this.getCampaign(callback)
        },
        setChanged(callback = null) {
            this.getCampaign(callback)
        },
        distributionCenterChanged(callback = null) {
            this.getCampaign(callback)
        },
        openCreateOrderByCampaignProductDialog() {
            this.showOrderByCampaignProductDialog = true
        },
        closeCreateOrderByCampaignProductDialog() {
            this.showOrderByCampaignProductDialog = false
        },
        createOrderByProductDialog() {
            this.getCampaign()
            this.closeCreateOrderByCampaignProductDialog()
        },
        generateReport() {
            this.loading = true
            store.dispatch('campaigns/generateCampaignReport', {
                'campaignId': this.campaign.id,
                'userEmail': this.userEmail
            })
                .then(() => {
                    this.loading = false
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        generateLabelsReport() {
            this.loading = true
            store.dispatch('campaigns/generateCampaignLabelsReport', {
                'campaignId': this.campaign.id,
                'userEmail': this.userEmail
            })
                .then(() => {
                    this.loading = false
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        tryCloseCampaign(campaign) {
            this.confirmDialogData = {
                title: 'Zamknięcie kampanii',
                content: 'Czy na pewno chcesz zamknąć kampanię?',
                confirm: this.closeCampaign,
                data: campaign,
            }
            this.showConfirmDialog = true
        },
        confirmDialogConfirm(payload) {
            this.confirmDialogData.confirm(payload)
            this.confirmDialogClose()
        },
        confirmDialogClose() {
            this.showConfirmDialog = false
            setTimeout(() => {
                this.confirmDialogData = {
                    title: null,
                    content: null,
                    confirm: () => {},
                    data: null,
                }
            }, 500)
        },
        closeCampaign({ data }) {
            this.loadingCampaignClose = true
            this.$store.dispatch('campaigns/updateCampaign', {
                id: data.id,
                statusId: 30, // 30 - zamknięta
            })
                .then(() => {
                    this.getCampaign(() => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Kampania została zamknięta',
                        })

                        this.loadingCampaignClose = false
                    })
                })
                .catch(error => {
                    errorHandler(error)

                    this.loadingCampaignClose = false
                })
        },
    },
}
</script>
